import BarcodeTopper from "components/BarcodeTopper"
import PrimaryCTA from "components/buttons/PrimaryCTA"
import { graphql, useStaticQuery } from "gatsby"
import gsap from "gsap"
import DrawSVGPlugin from "gsap/DrawSVGPlugin"
import { ReactComponent as ERPConnectionsSVG } from "images/ERPConnections.svg"
import { ReactComponent as HRISConnectionsSVG } from "images/HRISConnections.svg"
import { ReactComponent as VendorConnectionsSVG } from "images/VendorConnections.svg"
import { ReactComponent as ArrowSVG } from "images/arrowRight.svg"
import { ReactComponent as ADPSVG } from "images/companies/adp.svg"
import { ReactComponent as GlobalIndustrialSVG } from "images/companies/globalIndustrial.svg"
import { ReactComponent as GoogleWorkspaceSVG } from "images/companies/googleWorkspace.svg"
import { ReactComponent as GraingerSVG } from "images/companies/grainger.svg"
import { ReactComponent as MicrosoftDynamicsSVG } from "images/companies/microsoftDynamics.svg"
import { ReactComponent as OktaSVG } from "images/companies/okta.svg"
import { ReactComponent as OracleSVG } from "images/companies/oracle.svg"
import { ReactComponent as QuickBooksSVG } from "images/companies/quickbooks.svg"
import { ReactComponent as RipplingSVG } from "images/companies/rippling.svg"
import { ReactComponent as SageSVG } from "images/companies/sage.svg"
import { ReactComponent as StaplesSVG } from "images/companies/staples.svg"
import { ReactComponent as WorkdaySVG } from "images/companies/workday.svg"
import { ReactComponent as ZohoSVG } from "images/companies/zoho.svg"
import ConstantMarquee from "library/ConstantMarquee"
import UniversalImage from "library/UniversalImage"
import { svgColor } from "library/cssHelpers"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useAnimation from "library/useAnimation"
import useMedia from "library/useMedia"
import { getResponsivePixels } from "library/viewportUtils"
import { type MutableRefObject, useRef, useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"

gsap.registerPlugin(DrawSVGPlugin)

export default function ShoppingCompanion() {
	const wrapperRef = useRef<HTMLDivElement>(null)
	const fullWidth = useMedia(true, false, false, false)
	const ERPLogosRef = useRef<HTMLDivElement>(null)
	const VendorLogosRef = useRef<HTMLDivElement>(null)
	const HRISLogosRef = useRef<HTMLDivElement>(null)
	const ERPBulletRef = useRef<HTMLDivElement>(null)
	const VendorBulletRef = useRef<HTMLDivElement>(null)
	const HRISBulletRef = useRef<HTMLDivElement>(null)
	const ERPSquareRef = useRef<HTMLDivElement>(null)
	const VendorSquareRef = useRef<HTMLDivElement>(null)
	const HRISSquareRef = useRef<HTMLDivElement>(null)
	const ERPConnectionsRef = useRef<HTMLDivElement>(null)
	const VendorConnectionsRef = useRef<HTMLDivElement>(null)
	const HRISConnectionsRef = useRef<HTMLDivElement>(null)
	const [activeSquare, setActiveSquare] = useState("")
	const mobile = useMedia(false, false, false, true)

	const images: Queries.ShoppingCompanionQuery = useStaticQuery(graphql`
		query ShoppingCompanion {
			product: file(relativePath: { eq: "Product.png" }) {
				childImageSharp {
					gatsbyImageData(placeholder: NONE)
				}
			}
			whiteSteps: file(relativePath: { eq: "WhiteSteps.png" }) {
				childImageSharp {
					gatsbyImageData(placeholder: NONE)
				}
			}
			whiteStepsFullWidth: file(
				relativePath: { eq: "WhiteStepsFullWidth.png" }
			) {
				childImageSharp {
					gatsbyImageData(placeholder: NONE)
				}
			}
		}
	`)

	useAnimation(() => {
		if (
			!ERPLogosRef.current ||
			!HRISLogosRef.current ||
			!VendorLogosRef.current
		)
			return

		const tl = gsap.timeline({ repeat: -1 })

		tl.to(
			[ERPLogosRef.current],
			{
				delay: 0.9,
				yPercent: mobile ? -23 : -22,
				duration: 2,
				ease: "power3.inOut",
			},
			0,
		)

		tl.to(
			[ERPLogosRef.current],
			{
				delay: 1,
				yPercent: mobile ? -46 : -45,
				duration: 2.1,
				ease: "power3.inOut",
			},
			2.8,
		)

		tl.to(
			[ERPLogosRef.current],
			{
				delay: 0.9,
				yPercent: mobile ? -68 : -67,
				duration: 2.1,
				ease: "power3.inOut",
			},
			5.5,
		)

		tl.to(
			[ERPLogosRef.current],
			{
				delay: 0.8,
				yPercent: mobile ? -91 : -89.5,
				duration: 2.1,
				ease: "power3.inOut",
			},
			8.2,
		)

		const tl2 = gsap.timeline({ repeat: -1 })

		tl2.to(
			VendorLogosRef.current,
			{
				delay: 1,
				yPercent: mobile ? -29 : -28,
				duration: 2.5,
				ease: "power3.inOut",
			},
			0,
		)

		tl2.to(
			VendorLogosRef.current,
			{
				delay: 1,
				yPercent: mobile ? -58.5 : -57,
				duration: 2.5,
				ease: "power3.inOut",
			},
			3.1,
		)

		tl2.to(
			VendorLogosRef.current,
			{
				delay: 1,
				yPercent: mobile ? -88.5 : -86.5,
				duration: 2.5,
				ease: "power3.inOut",
			},
			6.2,
		)

		const tl3 = gsap.timeline({ repeat: -1 })

		tl3.to(
			[HRISLogosRef.current],
			{
				delay: 0.95,
				yPercent: mobile ? -41 : -40,
				duration: 2,
				ease: "power3.inOut",
			},
			0,
		)

		tl3.to(
			[HRISLogosRef.current],
			{
				delay: 0.95,
				yPercent: mobile ? -83.75 : -81.25,
				duration: 2,
				ease: "power3.inOut",
			},
			3.5,
		)
	}, [mobile])

	useAnimation(
		() => {
			const connectionsAnimation = (className: string) => {
				gsap.fromTo(
					className,
					{ drawSVG: "0% 12%" },
					{
						drawSVG: "88% 100%",
						duration: 1,
						repeat: 3,
						stagger: gsap.utils.random(0.3, 0.6),
					},
				)
			}

			const plugAndPlayAnimation = (
				bulletRef: MutableRefObject<HTMLDivElement | null>,
				squareRef: MutableRefObject<HTMLDivElement | null>,
				connectionsRef: MutableRefObject<HTMLDivElement | null>,
				activeSquare: string,
				className: string,
			) => {
				const childTimeline = gsap.timeline()

				childTimeline.to(
					bulletRef.current,
					{
						duration: 2,
						ease: "power3.inOut",
						color: colors.charcoal,
					},
					0,
				)

				childTimeline.to(
					`.${activeSquare}-arrow`,
					{
						width: getResponsivePixels(20),
						height: getResponsivePixels(13),
						duration: 1.5,
						ease: "power2.inOut",
					},
					0.5,
				)

				childTimeline.to(
					squareRef.current,
					{
						scale: 1.33,
						duration: 1,
						onStart: () => {
							connectionsAnimation(className)
						},
						onComplete: () => {
							setActiveSquare(activeSquare)
						},
					},
					"<1",
				)

				childTimeline.to(
					connectionsRef.current,
					{
						opacity: 1,
						duration: 1,
					},
					"<",
				)

				childTimeline.to(
					squareRef.current,
					{
						scale: 1,
						duration: 1,
						onStart: () => {
							setActiveSquare("")
						},
					},
					">2",
				)

				childTimeline.to(
					connectionsRef.current,
					{
						opacity: 0,
						duration: 1,
					},
					"<",
				)

				childTimeline.to(
					bulletRef.current,
					{
						duration: 1,
						ease: "power3.inOut",
						color: colors.grey2,
					},
					">-0.5",
				)

				childTimeline.to(
					`.${activeSquare}-arrow`,
					{
						width: getResponsivePixels(0),
						height: getResponsivePixels(13),
						duration: 1.5,
						ease: "power2.inOut",
					},
					"<",
				)

				return childTimeline
			}

			if (!connectionsAnimation) return
			const parentTimeline = gsap.timeline({ repeat: -1 })

			parentTimeline.add(
				plugAndPlayAnimation(
					ERPBulletRef,
					ERPSquareRef,
					ERPConnectionsRef,
					"ERP",
					".blue1",
				),
			)

			parentTimeline.add(
				plugAndPlayAnimation(
					VendorBulletRef,
					VendorSquareRef,
					VendorConnectionsRef,
					"Vendor",
					".blue2",
				),
			)

			parentTimeline.add(
				plugAndPlayAnimation(
					HRISBulletRef,
					HRISSquareRef,
					HRISConnectionsRef,
					"HRIS",
					".blue3",
				),
			)
		},
		[],
		{ scope: wrapperRef },
	)

	return (
		<Wrapper ref={wrapperRef}>
			{fullWidth ? (
				<StepsFullWidth
					image={images.whiteStepsFullWidth}
					alt="Background Accent"
				/>
			) : (
				<Steps image={images.whiteSteps} alt="Background Accent" />
			)}
			<Inner>
				<Top>
					<Content>
						<StyledTopper>Integrations</StyledTopper>
						<Title>Plug and Play</Title>
						<Text>
							Don&apos;t change the way you do business. Seamlessly integrate
							with your systems and vendors in minutes, not months.
						</Text>
						<Bullets>
							<Bullet ref={ERPBulletRef}>
								<BulletArrow className="ERP-arrow" />
								ERP
							</Bullet>
							<Bullet ref={VendorBulletRef}>
								<BulletArrow className="Vendor-arrow" />
								Vendor
							</Bullet>
							<Bullet ref={HRISBulletRef}>
								<BulletArrow className="HRIS-arrow" />
								HRIS
							</Bullet>
						</Bullets>
						<div>
							<StyledButton type="calendly">Learn More</StyledButton>
						</div>
					</Content>
					<Right>
						<ERPSquare ref={ERPSquareRef}>
							<Logos ref={ERPLogosRef} $isActive={activeSquare === "ERP"}>
								<SageSVG />
								<OracleSVG />
								<MicrosoftDynamicsSVG />
								<WorkdaySVG />
								<SageSVG />
							</Logos>
						</ERPSquare>
						<VendorSquare ref={VendorSquareRef}>
							<Logos ref={VendorLogosRef} $isActive={activeSquare === "Vendor"}>
								<StaplesSVG />
								<GraingerSVG />
								<GlobalIndustrialSVG />
								<StaplesSVG />
							</Logos>
						</VendorSquare>
						<HRISSquare ref={HRISSquareRef}>
							<Logos ref={HRISLogosRef} $isActive={activeSquare === "HRIS"}>
								<RipplingSVG />
								<ADPSVG />
								<RipplingSVG />
							</Logos>
						</HRISSquare>
						<ImageWrapper>
							<SVGWrapper ref={ERPConnectionsRef}>
								<ERPConnections />
							</SVGWrapper>
							<SVGWrapper ref={VendorConnectionsRef}>
								<VendorConnections />
							</SVGWrapper>
							<SVGWrapper ref={HRISConnectionsRef}>
								<HRISConnections />
							</SVGWrapper>
							<Image image={images.product} alt="" />
						</ImageWrapper>
					</Right>
				</Top>
				<Marquee timing={32}>
					<MarqueeContent>
						<QuickBooksSVG />
						<ADPSVG />
						<ZohoSVG />
						<RipplingSVG />
						<OktaSVG />
						<GoogleWorkspaceSVG />
						<WorkdaySVG />
						<OracleSVG />
						<SageSVG />
						<MicrosoftDynamicsSVG />
					</MarqueeContent>
				</Marquee>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled.section`
	${fresponsive(css`
		position: relative;
		height: 1070px;
	`)}

	${ftablet(css`
		height: 1718px;
	`)}
`

const Steps = styled(UniversalImage)`
	${fresponsive(css`
		position: absolute;
		width: 960px;
		height: 164px;
		left: -67px;
		top: -26px;
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const StepsFullWidth = styled(Steps)`
	${fresponsive(css`
		width: 1560px;
		left: -667px;
	`)}
`

const Inner = styled.div`
	${fresponsive(css`
		padding: 90px 40px 101px;
	`)}

	${ftablet(css`
		padding: 235px 0 103px;
	`)}

	${fmobile(css`
		padding: 130px 0 42px;
	`)}
`

const Top = styled.div`
	${fresponsive(css`
		display: flex;
		gap: 59px;
		align-items: flex-end;
		height: fit-content;
		width: 100%;
	`)}

	${ftablet(css`
		flex-direction: column;
		align-items: center;
		gap: 72px;
	`)}

	${fmobile(css`
		flex-direction: column;
		align-items: center;
		gap: 60px;
	`)}
`

const Content = styled.div`
	${fresponsive(css`
		display: flex;
		flex-direction: column;
		gap: 50px;
		width: 560px;
	`)}

	${ftablet(css`
		gap: 48px;
		text-align: center;
		align-items: center;
	`)}

	${fmobile(css`
		gap: 24px;
		text-align: center;
		align-items: center;
	`)}
`

const StyledTopper = styled(BarcodeTopper)`
	${fresponsive(css`
		margin-bottom: 0;
		color: ${colors.grey2};

		svg {
			path {
				fill: ${colors.grey2};
			}
		}
	`)}

	${ftablet(css`
		border-bottom: none;
		padding-bottom: 0;
	`)}

	${fmobile(css`
		border-bottom: none;
		padding-bottom: 0;
	`)}
`

const Title = styled.div`
	${fresponsive(css`
		${textStyles.h2uncapped};
		color: ${colors.grey4};
		margin: -10px 0;
	`)}

	${fmobile(css`
		${textStyles.h4};
		margin: -4px 0;
	`)}
`

const Text = styled.div`
	${fresponsive(css`
		${textStyles.p1};
		color: ${colors.grey3};
		width: 364px;
		margin: -4px 0;
	`)}

	${fmobile(css`
		${textStyles.p2};
		width: 280px;
	`)}
`

const StyledButton = styled(PrimaryCTA)`
	${fresponsive(css`
		padding: 25px 45px;
	`)}
`

const Right = styled.div`
	${fresponsive(css`
		position: relative;
		display: flex;
		justify-content: flex-end;
		align-items: flex-end;
		width: 741px;
		height: 645px;
	`)}

	${ftablet(css`
		margin-right: 45px;
	`)}

	${fmobile(css`
		left: 100%;
		transform: translateX(-50%);
		width: 556px;
		height: 425px;
	`)}
`

const Image = styled(UniversalImage)`
	${fresponsive(css`
		width: 1202px;
		height: auto;
		position: absolute;
		scale: 1.1;
		z-index: 3;
		left: 70px;
		top: 86px;
	`)}

	${fmobile(css`
		scale: 0.8;
		left: -107px;
		top: -40px;
	`)}
`

const ImageWrapper = styled.div`
	border: 1px solid ${colors.grey1};

	${fresponsive(css`
		position: relative;
		width: 668px;
		height: 599px;
		border-radius: 24px;
		background: ${colors.grey1};
		overflow: clip;
		z-index: 1;
	`)}

	${fmobile(css`
		width: 501px;
		height: 399px;
		border-radius: 18px;
	`)}
`

const Marquee = styled(ConstantMarquee)`
	${fresponsive(css`
		align-items: center;
		overflow: clip;
		width: 1400px;
		height: 115px;
		margin-top: 119px;
		border-top: 1px solid ${colors.grey1};

		&::before {
			content: "";
			z-index: 2;
			position: absolute;
			top: 0;
			left: -2px;
			width: 50px;
			height: 100%;
			background: linear-gradient(
				to left,
				transparent 0%,
				${colors.alpine} 100%
			);
		}

		&::after {
			content: "";
			z-index: 2;
			position: absolute;
			top: 0;
			right: -2px;
			width: 50px;
			height: 100%;
			background: linear-gradient(
				to right,
				transparent 0%,
				${colors.alpine} 100%
			);
		}
	`)}

	${ftablet(css`
		margin-top: 135px;
		width: 944px;
	`)}

	${fmobile(css`
		margin-top: 50px;
		width: 342px;
	`)}
`

const MarqueeContent = styled.div`
	${fresponsive(css`
		display: flex;
		gap: 120px;
		padding-left: 120px;

		svg {
			width: 130px;
			height: 51px;
		}
	`)}

	${fmobile(css`
		gap: 60px;
		padding-left: 60px;
	`)}
`

const IntegrationSquare = styled.div`
	border: 0.75px solid ${colors.grey1};
	background: ${colors.alpine};
	box-shadow: 0 15px 45px 0 rgba(0 0 0 / 8%);

	${fresponsive(css`
		position: absolute;
		z-index: 3;
		width: 72px;
		height: 72px;
		border-radius: 9px;
		overflow: clip;
		display: flex;
		justify-content: center;
	`)}

	${fmobile(css`
		width: 54px;
		height: 54px;
		border-radius: 7px;
		box-shadow: 0 15px 45px 0 rgba(0 0 0 / 8%);
	`)}
`

const ERPSquare = styled(IntegrationSquare)`
	${fresponsive(css`
		left: 20px;
		bottom: 114px;
	`)}

	${fmobile(css`
		left: 12px;
		bottom: 30px;
	`)}
`

const VendorSquare = styled(IntegrationSquare)`
	${fresponsive(css`
		top: 0;
		left: 150px;
	`)}

	${fmobile(css`
		left: 119px;
	`)}
`

const HRISSquare = styled(IntegrationSquare)`
	${fresponsive(css`
		top: 23px;
		right: 276px;
	`)}

	${fmobile(css`
		top: 18px;
		right: 204px;
	`)}
`

const Logos = styled.div<{ $isActive: boolean }>`
	${({ $isActive }) =>
		fresponsive(css`
			position: absolute;
			top: 28%;
			display: flex;
			flex-direction: column;
			gap: 35px;

			svg {
				width: 75px;
				height: 30px;
				${svgColor($isActive ? colors.blue3 : colors.grey2)};

				path {
					transition: fill 1s;
				}
			}
		`)}

	${fmobile(css`
		top: 30%;

		svg {
			width: 56px;
			height: 22px;
		}
	`)}
`

const Bullets = styled.div`
	${fresponsive(css`
		color: ${colors.grey2};
		${textStyles.kicker1};
		display: flex;
		gap: 24px;
	`)}
`

const BulletArrow = styled(ArrowSVG)`
	${fresponsive(css`
		width: 0;
		height: 13px;
	`)}
`

const Bullet = styled.div`
	${fresponsive(css`
		display: flex;
		gap: 12px;
		justify-content: flex-end;
		align-items: center;
	`)}
`

const SVGWrapper = styled.div`
	${fresponsive(css`
		position: absolute;
		width: 100%;
		height: 100%;
		opacity: 0;
	`)}
`

const connectionsBase = css`
	${fresponsive(css`
		position: absolute;
		height: auto;
		z-index: 2;
	`)}
`

const ERPConnections = styled(ERPConnectionsSVG)`
	${connectionsBase};

	${fresponsive(css`
		width: 95px;
		left: 2px;
		bottom: 127px;
	`)}

	${fmobile(css`
		width: 71px;
		bottom: 88px;
	`)}
`

const VendorConnections = styled(VendorConnectionsSVG)`
	${connectionsBase};

	${fresponsive(css`
		width: 96px;
		top: 3px;
		left: 90px;
	`)}

	${fmobile(css`
		width: 70px;
		left: 75px;
		top: 12px;
	`)}
`

const HRISConnections = styled(HRISConnectionsSVG)`
	${connectionsBase};

	${fresponsive(css`
		width: 121px;
		top: 17px;
		right: 257px;
	`)}

	${fmobile(css`
		width: 90px;
		right: 188px;
	`)}
`
